import * as React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "../lib/utils";

export interface BreadcrumbItem {
  label: React.ReactNode;
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Custom link renderer (host app injects its router <Link>). */
  linkComponent?: React.ComponentType<
    React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }
  >;
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  /** App-wide default link component; per-item linkComponent wins if set. */
  defaultLinkComponent?: React.ComponentType<
    React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }
  >;
}

const Breadcrumbs = React.forwardRef<HTMLElement, BreadcrumbsProps>(
  ({ className, items, separator, defaultLinkComponent, ...props }, ref) => (
    <nav
      ref={ref}
      aria-label="Breadcrumb"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    >
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          const LinkComp = item.linkComponent ?? defaultLinkComponent;
          return (
            <li key={i} className="flex items-center gap-1.5">
              {isLast ? (
                <span aria-current="page" className="font-medium text-foreground">
                  {item.label}
                </span>
              ) : item.href ? (
                LinkComp ? (
                  <LinkComp
                    href={item.href}
                    onClick={item.onClick}
                    className="rounded-md px-1 py-0.5 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {item.label}
                  </LinkComp>
                ) : (
                  <a
                    href={item.href}
                    onClick={item.onClick}
                    className="rounded-md px-1 py-0.5 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {item.label}
                  </a>
                )
              ) : item.onClick ? (
                <button
                  type="button"
                  onClick={item.onClick}
                  className="rounded-md px-1 py-0.5 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {item.label}
                </button>
              ) : (
                <span className="px-1 py-0.5">{item.label}</span>
              )}
              {!isLast ? (
                <span aria-hidden="true" className="flex items-center text-muted-foreground/60">
                  {separator ?? <ChevronRight className="size-3.5" />}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  ),
);
Breadcrumbs.displayName = "Breadcrumbs";

export { Breadcrumbs };
